// /account/subscription/:deviceId — 訂閱狀態 + 配額儀表板 + 帳單（spec 19）。
//   - 訂閱卡：GET /v3/devices/{id}/subscription（00 §9.1 SubscriptionView；404 = 無訂閱）
//     status → UI 映射照 19 §3.5；顯示一律以 REST 回應為真值（ADR-007，不本地推算）。
//   - 配額：GET /v3/me/quota?device_id=（02 §7.2）；limit=null → 只顯示 used、無進度條
//     （19 §5.1 limit=null 安全語意）；端點不可用 → 整段優雅隱藏。
//   - 降級（ADR-025 模式 B）：任一維度 over → 紅色說明 banner；warning → 黃色。
//   - 帳單：GET /v3/billing/invoices（00 §9.1 固定近 20 筆）；amount 為最小貨幣單位
//     整數，依 ISO 4217 minor-unit 格式化、全程整數運算（19 §4.2）；失敗 → 隱藏。
//   - D-1 未決：購買/續訂 CTA 一律 gated「即將開放」，不做任何金流。
//   - shared-user mask（19 AC-5）：非 owner 不顯示訂閱/配額/帳單。

import { hasBrandCap } from '../../lib/brand';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ApiError, apiFetch, addSubscriptionDevice, cancelSubscriptionDev, claimFreeSubscription, freeEligibility, listSubscriptions, removeSubscriptionDevice, startSubscriptionCheckout, startSubscriptionPortal, type EffectiveEntitlementExplain, type PlanEntitlements, type UserSubscriptionView } from '../../lib/api';
import { signaling } from '../../lib/ws';
import { opGet, opSetEnum, frameValue } from '../devices/settings/ops';
import { ConfirmDialog } from '../devices/settings/widgets';
import { SD_PRESENT, REC_LOCATION, REC_MODE, REC_LOCATION_ENUM, REC_MODE_ENUM } from '../devices/settings/ids';
import { cloudSetupIntent } from '../devices/settings/cloudSetupIntent';
import { config } from '../../config';
import { formatTimestampUs } from '../../lib/format';

// ---- types (00 §9.1 / 02 §7.2) ----

interface SubscriptionView {
  plan?: string;
  plan_id?: string;
  plan_version?: number;
  status?: string;
  expired_time?: number;
  auto_renew?: boolean;
  retention_days?: number;
  quality_allowed?: string;
  storage_gb_limit?: number;
  relay_gb_limit?: number;
  device_share_limit?: number;
  plan_entitlements?: PlanEntitlements;
}

// PlanEntitlements / ExplainStage / EffectiveEntitlementExplain moved to lib/api.ts
// (exported) so the device-settings recording section can share the same shapes.

interface PlanPrice {
  region?: string;
  currency?: string;
  amount_minor?: number;
  billing_interval?: string;
}

interface PlanDefinition {
  plan_id: string;
  version: number;
  name: string;
  billing_interval?: string;
  purchasable?: boolean;
  /** OPTIONAL — true marks the one-time free plan claimed via :claim-free (00 §9.1). Absent/false = paid. */
  default_free?: boolean;
  entitlements?: PlanEntitlements;
  prices?: PlanPrice[];
}

interface QuotaDim {
  used?: number | null;
  limit?: number | null;
  remaining?: number | null;
  usage_ratio?: number | null;
  status?: string; // ok | warning | over | unknown
  as_of?: number;
  period_start?: number;
  period_end?: number;
}

interface QuotaEntry {
  device_id?: string;
  plan?: string;
  subscription_status?: string;
  quota?: Record<string, QuotaDim>;
}

interface BillingStatement {
  account?: { currency?: string };
  invoices?: Array<{ invoice_id: string; status?: string; total_minor?: number; currency?: string; created_at?: number; lines?: Array<{ plan_id?: string; plan_version?: number; description?: string }> }>;
  payments?: Array<{ payment_id: string; status?: string; amount_minor?: number; currency?: string; occurred_at?: number }>;
  refunds?: Array<{ refund_id: string; status?: string; amount_minor?: number; currency?: string; occurred_at?: number; reason?: string }>;
  chargebacks?: Array<{ chargeback_id: string; status?: string; amount_minor?: number; currency?: string; occurred_at?: number; reason?: string }>;
  credits?: Array<{ credit_id: string; status?: string; remaining_minor?: number; amount_minor?: number; currency?: string; created_at?: number; reason?: string }>;
  grants?: Array<{ grant_id: string; status?: string; plan_id?: string; plan_version?: number; source?: string; effective_from?: number; effective_to?: number; subject_type?: string; subject_id?: string }>;
}

export interface BillingRow {
  id: string;
  ts?: number;
  type: string;
  detail: string;
  amountMinor?: number;
  currency?: string;
}

export function statementRows(statement: BillingStatement | null): BillingRow[] {
  if (!statement) return [];
  const currency = statement.account?.currency;
  return [
    ...(statement.invoices ?? []).map((v) => ({ id: v.invoice_id, ts: v.created_at, type: `invoice · ${v.status ?? '—'}`, detail: v.lines?.map((l) => l.description || `${l.plan_id ?? 'plan'}@${l.plan_version ?? '—'}`).join('、') || v.invoice_id, amountMinor: v.total_minor, currency: v.currency ?? currency })),
    ...(statement.payments ?? []).map((v) => ({ id: v.payment_id, ts: v.occurred_at, type: `payment · ${v.status ?? '—'}`, detail: v.payment_id, amountMinor: v.amount_minor, currency: v.currency ?? currency })),
    ...(statement.refunds ?? []).map((v) => ({ id: v.refund_id, ts: v.occurred_at, type: `refund · ${v.status ?? '—'}`, detail: v.reason || v.refund_id, amountMinor: v.amount_minor == null ? undefined : -v.amount_minor, currency: v.currency ?? currency })),
    ...(statement.chargebacks ?? []).map((v) => ({ id: v.chargeback_id, ts: v.occurred_at, type: `chargeback · ${v.status ?? '—'}`, detail: v.reason || v.chargeback_id, amountMinor: v.amount_minor == null ? undefined : -v.amount_minor, currency: v.currency ?? currency })),
    ...(statement.credits ?? []).map((v) => ({ id: v.credit_id, ts: v.created_at, type: `credit · ${v.status ?? '—'}`, detail: v.reason || v.credit_id, amountMinor: v.remaining_minor ?? v.amount_minor, currency: v.currency ?? currency })),
    ...(statement.grants ?? []).map((v) => ({ id: v.grant_id, ts: v.effective_from, type: `grant · ${v.status ?? '—'}`, detail: `${v.plan_id ?? 'plan'}@${v.plan_version ?? '—'} · ${v.source ?? 'grant'}`, currency })),
  ].sort((a, b) => (b.ts ?? 0) - (a.ts ?? 0));
}

interface DeviceLite {
  device_id: string;
  display_name?: string;
  aliasname?: string;
  role?: string;
}

function deviceLiteLabel(d: DeviceLite): string {
  return d.aliasname || d.display_name || d.device_id;
}

// ---- status → UI（19 §3.5） ----

export function statusUi(s: SubscriptionView, t: TFunction): { label: string; color: string } {
  const expiry = s.expired_time != null ? formatTimestampUs(s.expired_time, { second: undefined, hour: undefined, minute: undefined }) : '—';
  switch (s.status) {
    case 'trialing':
      return { label: t('account.subStatusTrialing', { expiry }), color: 'var(--color-semantic-info)' };
    case 'active':
      return {
        label: s.auto_renew ? t('account.subStatusActiveRenew', { expiry }) : t('account.subStatusActiveUntil', { expiry }),
        color: 'var(--color-semantic-success)',
      };
    case 'past_due':
      return { label: t('account.subStatusPastDue'), color: 'var(--color-semantic-warning)' };
    case 'grace':
      return { label: t('account.subStatusGrace', { expiry }), color: 'var(--color-semantic-warning)' };
    case 'cancelled':
      return { label: t('account.subStatusCancelled', { expiry }), color: 'var(--color-text-secondary)' };
    case 'expired':
      return { label: t('account.subStatusExpired'), color: 'var(--color-semantic-danger)' };
    default:
      return { label: s.status ?? '—', color: 'var(--color-text-secondary)' };
  }
}

// ---- 金額格式化（19 §4.2：minor units、整數運算、不做匯率換算） ----

const ZERO_DECIMAL = new Set(['TWD', 'JPY', 'KRW', 'VND']); // per spec 19 §4.2 規則表（TWD ÷1、JPY ÷1）

export function formatAmount(minor: number | undefined, currency: string | undefined): string {
  if (minor === undefined || !Number.isFinite(minor)) return '—';
  const cur = (currency ?? '').toUpperCase();
  if (ZERO_DECIMAL.has(cur)) return `${cur} ${minor}`;
  const sign = minor < 0 ? '-' : '';
  const abs = Math.abs(minor);
  const whole = Math.trunc(abs / 100);
  const frac = String(abs % 100).padStart(2, '0');
  return `${cur} ${sign}${whole}.${frac}`;
}

const DIMENSION_LABEL_KEY: Record<string, string> = {
  storage_gb: 'account.dimStorage',
  relay_gb: 'account.dimRelay',
  share_count: 'account.dimShareCount',
};

const DIMENSION_UNIT_KEY: Record<string, string> = {
  storage_gb: 'account.unitGb',
  relay_gb: 'account.unitGb',
  share_count: 'account.unitPersons',
};

function dimColor(status: string | undefined): string {
  switch (status) {
    case 'ok':
      return 'var(--color-semantic-success)';
    case 'warning':
      return 'var(--color-semantic-warning)';
    case 'over':
      return 'var(--color-semantic-danger)';
    default:
      return 'var(--color-text-secondary)';
  }
}

// ---- cloud-plan memberships（帳號層 /v3/subscriptions；有別於本頁 per-device 訂閱卡） ----
// 顯示帳號名下方案 + 每方案的裝置指派（attach/detach）。純顯示＋管理，無金流。
// 契約缺口：UserSubscriptionView 只帶 device_count，未含「已指派裝置清單」，因此無法
// 以 chip 列出已綁裝置；以擁有者裝置選單搭配 加入/移除 兩顆按鈕操作，回應即真值。

const MEMBERSHIP_ACTIVE = new Set(['active', 'trialing']);

// R4 helper: the account page holds no device signaling room, so probing SD presence and
// writing the record MODE/LOCATION after a detach needs the socket open first. Mirror the
// live-session waitForOpen pattern (connect if needed, resolve on 'open', reject on close/
// timeout). Best-effort at the call site — a rejection falls back to stopping recording.
const WSS_OPEN_TIMEOUT_MS = 8000;
function waitSignalingOpen(): Promise<void> {
  if (signaling.getState() === 'open') return Promise.resolve();
  signaling.connect();
  return new Promise((resolve, reject) => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    const off = signaling.on('state', (s) => {
      if (s === 'open') {
        off();
        if (timer) clearTimeout(timer);
        resolve();
      } else if (s === 'closed') {
        off();
        if (timer) clearTimeout(timer);
        reject(new Error('signaling closed'));
      }
    });
    timer = setTimeout(() => {
      off();
      reject(new Error('signaling open timeout'));
    }, WSS_OPEN_TIMEOUT_MS);
  });
}

function membershipStatusColor(status: string): string {
  switch (status) {
    case 'active':
    case 'trialing':
      return 'var(--color-semantic-success)';
    case 'past_due':
    case 'grace':
      return 'var(--color-semantic-warning)';
    case 'cancelled':
      return 'var(--color-text-secondary)';
    case 'expired':
      return 'var(--color-semantic-danger)';
    default:
      return 'var(--color-text-secondary)';
  }
}

export function MembershipSection() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [subs, setSubs] = useState<UserSubscriptionView[] | null>(null);
  // R1 resume: a device parked "awaiting cloud location" on the recording screen sent the
  // user here to buy/claim/attach. Show a banner and pre-select that device in every plan's
  // picker; attaching it returns to settings where the location becomes cloud. Read once.
  const [intentDevice] = useState<string | null>(() => cloudSetupIntent.get());
  // R2: after attach, if the device still records to SD → offer switching it to cloud.
  const [switchCloudFor, setSwitchCloudFor] = useState<string | null>(null);
  // R4: after detach of a cloud recorder, if an SD card is present → offer switching to SD;
  // decline / no card → recording MODE is set to none (stop). detachStopped drives the notice.
  const [detachSdFor, setDetachSdFor] = useState<{ subId: string; deviceId: string } | null>(null);
  const [detachBusy, setDetachBusy] = useState(false);
  const [detachStopped, setDetachStopped] = useState<string | null>(null);
  const [devices, setDevices] = useState<DeviceLite[]>([]);
  const [hidden, setHidden] = useState(false);
  const [selected, setSelected] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<Record<string, boolean>>({});
  const [errors, setErrors] = useState<Record<string, string | null>>({});
  const [cloudPlans, setCloudPlans] = useState<PlanDefinition[]>([]);
  const [buyPlan, setBuyPlan] = useState<string>('');
  const [buying, setBuying] = useState(false);
  const [buyErr, setBuyErr] = useState<string | null>(null);
  // One-time free plan (00 §9.1): card shown only when the account is still eligible AND
  // the catalog carries a default_free plan; its button claims via :claim-free (NOT Stripe).
  const [freeEligible, setFreeEligible] = useState(false);
  const [freePlan, setFreePlan] = useState<PlanDefinition | null>(null);
  const [claimingFree, setClaimingFree] = useState(false);
  const [freeErr, setFreeErr] = useState<string | null>(null);
  const [managing, setManaging] = useState<Record<string, boolean>>({});
  const [manageErr, setManageErr] = useState<Record<string, string | null>>({});
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const load = useCallback(async () => {
    const [subsR, devR, plansR, eligR] = await Promise.allSettled([
      listSubscriptions(),
      apiFetch<DeviceLite[]>('/v3/devices', { query: { include: 'subscription' } }),
      apiFetch<PlanDefinition[]>('/v3/plans'),
      freeEligibility(),
    ]);
    if (!alive.current) return;
    // 端點不可用 → 整段優雅隱藏（與本頁其它輔助卡一致的降級策略）。
    if (subsR.status === 'fulfilled') {
      setSubs(subsR.value);
      setHidden(false);
    } else {
      setSubs(null);
      setHidden(true);
    }
    if (devR.status === 'fulfilled' && Array.isArray(devR.value.data)) {
      setDevices(devR.value.data.filter((d) => d.role === 'owner'));
    }
    // Cloud plans available to buy on the web (Stripe) — recording-enabled + purchasable.
    if (plansR.status === 'fulfilled' && Array.isArray(plansR.value.data)) {
      // Exclude v1-standard (the legacy migration-baseline plan) from the buyable list, matching
      // iOS/Android — it is purchasable+recording but not sold on the app rails.
      const cloud = plansR.value.data.filter((p) => p.default_free !== true && p.plan_id !== 'v1-standard' && p.purchasable !== false && p.entitlements?.recording_enabled);
      setCloudPlans(cloud);
      setBuyPlan((prev) => prev || cloud[0]?.plan_id || '');
      // The one-time free plan is identified from the FULL catalog (NOT the paid filter)
      // strictly by default_free===true — never fold it into cloudPlans (that would route
      // it into Stripe checkout).
      const free = plansR.value.data.find((p) => p.default_free === true) ?? null;
      setFreePlan(free);
    }
    // Free-eligibility is best-effort: a rejected/absent endpoint → not eligible, never blocks the page.
    setFreeEligible(eligR.status === 'fulfilled' ? eligR.value.eligible === true : false);
  }, []);

  // Claim the one-time free plan. On success OR a swallowed 409 the reload re-fetches
  // subscriptions + eligibility → eligible flips false, the card disappears, and the new
  // membership shows up in the list. FREE_ALREADY_USED is a NORMAL terminal state (no toast).
  const claimFree = useCallback(async () => {
    setClaimingFree(true);
    setFreeErr(null);
    try {
      await claimFreeSubscription();
    } catch (err) {
      if (err instanceof ApiError && err.code === 'FREE_ALREADY_USED') {
        // normal terminal state — no error toast; the reload flips eligibility off.
      } else if (err instanceof ApiError) {
        if (alive.current) setFreeErr(err.message);
      } else if (alive.current) {
        setFreeErr(t('account.freeClaimFailed'));
      }
    } finally {
      if (alive.current) setClaimingFree(false);
      await load();
    }
  }, [t, load]);

  const buy = useCallback(async () => {
    const plan = cloudPlans.find((p) => p.plan_id === buyPlan);
    if (!plan) return;
    setBuying(true);
    setBuyErr(null);
    try {
      const url = await startSubscriptionCheckout(plan.plan_id, plan.version);
      window.location.href = url; // redirect to the Stripe-hosted checkout
    } catch (err) {
      if (!alive.current) return;
      setBuyErr(err instanceof ApiError ? err.message : t('account.membershipsBuyFailed'));
      setBuying(false);
    }
  }, [cloudPlans, buyPlan, t]);

  useEffect(() => {
    void load();
  }, [load]);

  // Route to the provider that sold the subscription (D32-R1 §8.1). Web memberships are
  // Stripe → open the Billing Portal (cancel / update payment / change plan live there).
  const manage = useCallback(
    async (sub: UserSubscriptionView) => {
      setManaging((m) => ({ ...m, [sub.subscription_id]: true }));
      setManageErr((e) => ({ ...e, [sub.subscription_id]: null }));
      try {
        const { portal_url } = await startSubscriptionPortal(sub.subscription_id);
        window.location.href = portal_url; // Stripe-hosted Billing Portal
      } catch (err) {
        if (!alive.current) return;
        const msg = err instanceof ApiError ? err.message : t('account.membershipManageFailed');
        setManageErr((e) => ({ ...e, [sub.subscription_id]: msg }));
        setManaging((m) => ({ ...m, [sub.subscription_id]: false }));
      }
    },
    [t],
  );

  // Durable plan removal — MUST run only AFTER the recording reconcile (below), never before:
  // removeSubscriptionDevice triggers the ADR-024 rev:device kick that drops the device off
  // signaling, so any op:set/SD-probe after it lands on an offline device.
  const removeFromPlan = useCallback(async (subId: string, deviceId: string) => {
    const updated = await removeSubscriptionDevice(subId, deviceId);
    if (!alive.current) return;
    setSubs((prev) => (prev ? prev.map((s) => (s.subscription_id === updated.subscription_id ? updated : s)) : prev));
  }, []);

  // R4 core: reconcile the device to sdcard/none while it is STILL ONLINE, THEN remove it from the
  // plan (mirror iOS reconcileThenRemove). Probing SD or writing settings after removeFromPlan would
  // always hit the just-kicked (offline) device — no "switch to SD" prompt, offline-persisted writes.
  // An SD-present device defers removal until the dialog choice (detachUseSdcard / detachStopRecording).
  const reconcileThenRemove = useCallback(
    async (subId: string, deviceId: string) => {
      setDetachStopped(null);
      // Probe SD while the device is still online (before the kick).
      let present: boolean | null = null;
      try {
        await waitSignalingOpen();
        await signaling.subscribe(deviceId).catch(() => {});
        const v = frameValue(await opGet(deviceId, SD_PRESENT));
        present = v === true || v === 1;
      } catch {
        present = null; // probe failed → treat as "no usable card" → stop recording
      }
      if (!alive.current) return;
      if (present) {
        setDetachSdFor({ subId, deviceId }); // ask (switch to SD / stop); removal happens after the choice
        return;
      }
      // no card / unknown → set sdcard + stop (MODE=none) while online, THEN remove from plan.
      try {
        await waitSignalingOpen();
        await opSetEnum(deviceId, REC_LOCATION, REC_LOCATION_ENUM.sdcard);
        await opSetEnum(deviceId, REC_MODE, REC_MODE_ENUM.none);
      } catch {
        /* best-effort — the plan removal below still ends cloud on the device's next sign-in */
      }
      await removeFromPlan(subId, deviceId);
      if (alive.current) setDetachStopped(deviceId);
    },
    [removeFromPlan],
  );

  const mutate = useCallback(
    async (sub: UserSubscriptionView, kind: 'attach' | 'detach') => {
      const deviceId = selected[sub.subscription_id];
      if (!deviceId) return;
      setBusy((b) => ({ ...b, [sub.subscription_id]: true }));
      setErrors((e) => ({ ...e, [sub.subscription_id]: null }));
      try {
        if (kind === 'attach') {
          const updated = await addSubscriptionDevice(sub.subscription_id, deviceId);
          if (!alive.current) return;
          setSubs((prev) => (prev ? prev.map((s) => (s.subscription_id === updated.subscription_id ? updated : s)) : prev));
          // R2: attach does NOT silently change the recording location. If the device currently
          // records to SD card (or unset→default sdcard), ask whether to switch it to cloud. The
          // actual op:set happens on the settings screen (device room + owner context) via the R1
          // resume path — here we only decide whether to prompt.
          let loc = 'sdcard';
          try {
            const s = await apiFetch<{ record_location?: string }>(`/v3/devices/${encodeURIComponent(deviceId)}/settings`);
            loc = s.data?.record_location || 'sdcard'; // empty/unset → sdcard (parity w/ parseSettings)
          } catch {
            /* best-effort: treat as sdcard → offer the switch */
          }
          if (alive.current && loc !== 'cloud') setSwitchCloudFor(deviceId);
          return;
        }
        // R4 detach: only a cloud recorder needs reconcile (an SD recorder stays valid — never
        // silently stopped). Read location while the device is still online, then reconcile BEFORE
        // removal (reconcileThenRemove); a non-cloud device is removed directly.
        let loc = 'sdcard';
        try {
          const s = await apiFetch<{ record_location?: string }>(`/v3/devices/${encodeURIComponent(deviceId)}/settings`);
          loc = s.data?.record_location || 'sdcard';
        } catch {
          /* best-effort */
        }
        if (loc === 'cloud') {
          await reconcileThenRemove(sub.subscription_id, deviceId);
        } else {
          await removeFromPlan(sub.subscription_id, deviceId);
        }
      } catch (err) {
        if (!alive.current) return;
        const msg = err instanceof ApiError ? err.message : t('account.networkErrorRetry');
        setErrors((e) => ({ ...e, [sub.subscription_id]: msg }));
      } finally {
        if (alive.current) setBusy((b) => ({ ...b, [sub.subscription_id]: false }));
      }
    },
    [selected, reconcileThenRemove, removeFromPlan, t],
  );

  // R2 confirm: user wants the just-attached device to record to cloud. The op:set lives on the
  // settings screen (device room + owner context) → park the intent and navigate there; the R1
  // resume effect finishes with record_location=cloud. Cancel leaves the device on SD card.
  const confirmSwitchCloud = useCallback(() => {
    const deviceId = switchCloudFor;
    setSwitchCloudFor(null);
    if (!deviceId) return;
    cloudSetupIntent.set(deviceId);
    navigate(`/devices/${encodeURIComponent(deviceId)}`, { state: { applyCloudRecording: true } });
  }, [switchCloudFor, navigate]);

  // R4 dialog handlers (SD card present). Use SD card → set LOCATION=sdcard; stop → set MODE=none.
  // R4 dialog "use SD card": set LOCATION=sdcard (keep recording) while still online, THEN remove
  // from plan. Removal is deferred to here (not done at detach click) so the op:set lands online.
  const detachUseSdcard = useCallback(async () => {
    const ctx = detachSdFor;
    if (!ctx) return;
    setDetachBusy(true);
    try {
      await waitSignalingOpen();
      await signaling.subscribe(ctx.deviceId).catch(() => {});
      await opSetEnum(ctx.deviceId, REC_LOCATION, REC_LOCATION_ENUM.sdcard);
    } catch {
      /* best-effort */
    }
    try {
      await removeFromPlan(ctx.subId, ctx.deviceId);
    } catch {
      /* best-effort — a reload surfaces the true membership state */
    }
    if (!alive.current) return;
    setDetachBusy(false);
    setDetachSdFor(null);
  }, [detachSdFor, removeFromPlan]);

  // R4 dialog "stop": set LOCATION=sdcard + MODE=none (stop) while still online, THEN remove.
  const detachStopRecording = useCallback(async () => {
    const ctx = detachSdFor;
    if (!ctx) return;
    setDetachBusy(true);
    try {
      await waitSignalingOpen();
      await signaling.subscribe(ctx.deviceId).catch(() => {});
      await opSetEnum(ctx.deviceId, REC_LOCATION, REC_LOCATION_ENUM.sdcard);
      await opSetEnum(ctx.deviceId, REC_MODE, REC_MODE_ENUM.none);
    } catch {
      /* best-effort */
    }
    try {
      await removeFromPlan(ctx.subId, ctx.deviceId);
    } catch {
      /* best-effort */
    }
    if (!alive.current) return;
    setDetachBusy(false);
    setDetachSdFor(null);
    setDetachStopped(ctx.deviceId);
  }, [detachSdFor, removeFromPlan]);

  // DEV-ONLY test shortcut (moved here from the iOS app): cancel at period end or hard-remove a
  // membership via the backend's dev-gated /cancel route, so a tester can reset test subscriptions
  // from the browser without editing the DB or a store. Shown only on dev (config.apiBase host);
  // prod cancellation stays provider-owned (Billing Portal / store). See the gate below.
  const devCancel = useCallback(
    async (sub: UserSubscriptionView, mode: 'period_end' | 'immediate') => {
      setBusy((b) => ({ ...b, [sub.subscription_id]: true }));
      setErrors((e) => ({ ...e, [sub.subscription_id]: null }));
      try {
        await cancelSubscriptionDev(sub.subscription_id, mode);
        if (!alive.current) return;
        // Re-fetch: an immediate-cancelled sub drops out of liveSubs; period_end shows cancel_pending.
        await load();
      } catch (err) {
        if (!alive.current) return;
        const msg = err instanceof ApiError ? err.message : t('account.networkErrorRetry');
        setErrors((e) => ({ ...e, [sub.subscription_id]: msg }));
      } finally {
        if (alive.current) setBusy((b) => ({ ...b, [sub.subscription_id]: false }));
      }
    },
    [load, t],
  );

  if (hidden || subs === null) return null;
  // DEV-only surface: the /cancel route is dev-gated on the backend; only show the buttons when the
  // client targets a dev API host (never in prod, where cancellation is provider-owned).
  const devTools = config.apiBase.includes('-dev');
  // 只顯示「仍有效/服務中」的訂閱:隱藏 expired/cancelled/suspended 終局狀態,避免過期訂閱
  // 堆積把畫面越拉越長(歷史帳單另有頁面)。cancel_pending 的 status 仍是 active → 保留顯示。
  const liveSubs = subs.filter((s) => !['expired', 'cancelled', 'suspended'].includes(s.status));
  // Cross-plan occupancy (Surface B, parity with iOS/Android): map each attached device to the
  // membership holding it, from every active membership's enriched devices[]. A device attached to
  // a DIFFERENT membership is pre-disabled in the picker below ("其他方案使用中") instead of failing
  // with DEVICE_IN_OTHER_PLAN only after the user clicks 加入.
  const deviceToSub = new Map<string, string>();
  for (const s of liveSubs) for (const d of s.devices ?? []) deviceToSub.set(d, s.subscription_id);
  // R1: label for the "awaiting cloud" banner + pre-selection target. Only when it is one of
  // this account's owner devices (otherwise the intent is stale/irrelevant → no banner).
  const intentOwnerDevice = intentDevice ? devices.find((d) => d.device_id === intentDevice) ?? null : null;
  const intentDeviceLabel = intentOwnerDevice ? deviceLiteLabel(intentOwnerDevice) : null;

  return (
    <section className="card" style={{ display: 'grid', gap: 'var(--spacing-5)' }}>
      <div style={{ display: 'grid', gap: 'var(--spacing-2)' }}>
        <h2 style={{ margin: 0, fontSize: 'var(--font-size-xl)' }}>{t('account.membershipsTitle')}</h2>
        <p className="text-secondary" style={{ margin: 0, fontSize: 'var(--font-size-sm)' }}>
          {t('account.membershipsIntro')}
        </p>
      </div>

      {/* R1: a device is waiting for cloud recording — tell the user to buy/claim a plan and
          attach this device; it is pre-selected in every plan's picker below. */}
      {intentDeviceLabel && (
        <div
          role="status"
          style={{
            border: '1px solid var(--color-semantic-info)',
            borderRadius: 'var(--radius-md)',
            padding: 'var(--spacing-4) var(--spacing-5)',
            fontSize: 'var(--font-size-sm)',
          }}
        >
          {t('account.cloudSetupBanner', { device: intentDeviceLabel })}
        </div>
      )}

      {detachStopped && (
        <p className="text-secondary" role="status" style={{ margin: 0, fontSize: 'var(--font-size-sm)' }}>
          {t('account.detachStopped')}
        </p>
      )}

      {cloudPlans.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-4)', flexWrap: 'wrap' }}>
          <select
            value={buyPlan}
            onChange={(e) => setBuyPlan(e.target.value)}
            disabled={buying}
            aria-label={t('account.membershipsBuySelect')}
          >
            {cloudPlans.map((p) => (
              <option key={`${p.plan_id}@${p.version}`} value={p.plan_id}>
                {p.name || p.plan_id}
              </option>
            ))}
          </select>
          <button className="btn-primary" onClick={() => void buy()} disabled={buying || !buyPlan}>
            {buying ? t('account.membershipsBuying') : t('account.membershipsBuyCta')}
          </button>
          {buyErr && (
            <span style={{ color: 'var(--color-danger)', fontSize: 'var(--font-size-sm)' }}>{buyErr}</span>
          )}
        </div>
      )}

      {/* One-time free plan: distinct card, shown only while eligible AND a default_free plan
          exists. Its button claims via :claim-free — NEVER buy()/Stripe checkout. */}
      {freeEligible && freePlan && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-4)', flexWrap: 'wrap' }}>
          <span style={{ fontSize: 'var(--font-size-md)', fontWeight: 'var(--font-weight-semibold)' }}>{freePlan.name || freePlan.plan_id}</span>
          <button className="btn-primary" onClick={() => void claimFree()} disabled={claimingFree}>
            {claimingFree ? t('account.freeClaiming') : t('account.freeClaimCta')}
          </button>
          {freeErr && (
            <span style={{ color: 'var(--color-danger)', fontSize: 'var(--font-size-sm)' }}>{freeErr}</span>
          )}
        </div>
      )}

      {liveSubs.length === 0 ? (
        <p className="text-secondary" style={{ margin: 0 }}>
          {t('account.membershipsEmpty')}
        </p>
      ) : (
        liveSubs.map((sub) => {
          // R1 pre-selection: default this plan's device picker to the intent device (unless the
          // user has already chosen one here, or the intent device is held by a DIFFERENT plan).
          const preselect =
            intentOwnerDevice && (deviceToSub.get(intentOwnerDevice.device_id) ?? sub.subscription_id) === sub.subscription_id
              ? intentOwnerDevice.device_id
              : '';
          const sel = selected[sub.subscription_id] ?? preselect;
          const isBusy = busy[sub.subscription_id] === true;
          const err = errors[sub.subscription_id] ?? null;
          const full = sub.device_count >= sub.max_devices;
          const manageable = MEMBERSHIP_ACTIVE.has(sub.status);
          const isManaging = managing[sub.subscription_id] === true;
          const mErr = manageErr[sub.subscription_id] ?? null;
          // Provider that sold the sub owns its lifecycle. Web memberships are Stripe →
          // Billing Portal; apple/google were bought on mobile → manage in-store.
          const provider = sub.billing_provider ?? '';
          const isStripe = provider === 'stripe' || provider === '';
          // cancel_pending: still active & entitled until expire_at, renewal is off.
          const expiryLocal = formatTimestampUs(sub.expire_at, { second: undefined });
          const statusLabel = sub.cancel_pending
            ? t('account.membershipCancelPending', { expiry: expiryLocal })
            : i18n.exists(`account.membershipStatus.${sub.status}`)
              ? t(`account.membershipStatus.${sub.status}`)
              : sub.status;
          const statusColor = sub.cancel_pending ? 'var(--color-text-secondary)' : membershipStatusColor(sub.status);
          return (
            <div
              key={sub.subscription_id}
              style={{
                display: 'grid',
                gap: 'var(--spacing-3)',
                paddingTop: 'var(--spacing-4)',
                borderTop: '1px solid var(--color-surface-line)',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--spacing-4)', flexWrap: 'wrap' }}>
                <span style={{ fontSize: 'var(--font-size-lg)', fontWeight: 'var(--font-weight-semibold)' }}>
                  {sub.plan_id}
                  <span className="text-secondary" style={{ fontWeight: 'var(--font-weight-regular)' }}>
                    {' '}
                    @{sub.plan_version}
                  </span>
                </span>
                <span style={{ color: statusColor, fontSize: 'var(--font-size-md)' }}>{statusLabel}</span>
              </div>
              <dl
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'max-content 1fr',
                  gap: 'var(--spacing-2) var(--spacing-8)',
                  margin: 0,
                  fontSize: 'var(--font-size-md)',
                }}
              >
                <dt className="text-secondary">{t('account.expiryDate')}</dt>
                <dd style={{ margin: 0 }}>{formatTimestampUs(sub.expire_at, { second: undefined })}</dd>
                <dt className="text-secondary">{t('account.membershipDevices')}</dt>
                <dd style={{ margin: 0 }}>{t('account.membershipDeviceQuota', { count: sub.device_count, max: sub.max_devices })}</dd>
              </dl>

              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-3)', flexWrap: 'wrap' }}>
                <select
                  aria-label={t('account.membershipSelectDevice')}
                  value={sel}
                  disabled={isBusy || devices.length === 0}
                  onChange={(e) => setSelected((s) => ({ ...s, [sub.subscription_id]: e.target.value }))}
                  style={{
                    padding: 'var(--spacing-2) var(--spacing-4)',
                    borderRadius: 'var(--radius-md)',
                    border: '1px solid var(--color-surface-line)',
                    background: 'var(--color-surface-panel)',
                    color: 'var(--color-text-primary)',
                    fontSize: 'var(--font-size-md)',
                  }}
                >
                  <option value="">{t('account.membershipSelectDevice')}</option>
                  {devices.map((d) => {
                    const holder = deviceToSub.get(d.device_id);
                    const inOtherPlan = holder != null && holder !== sub.subscription_id;
                    return (
                      <option key={d.device_id} value={d.device_id} disabled={inOtherPlan}>
                        {deviceLiteLabel(d)}
                        {inOtherPlan ? ` · ${t('account.membershipDeviceInOtherPlan')}` : ''}
                      </option>
                    );
                  })}
                </select>
                <button
                  className="btn-primary"
                  disabled={
                    isBusy ||
                    !sel ||
                    full ||
                    !manageable ||
                    (deviceToSub.get(sel) != null && deviceToSub.get(sel) !== sub.subscription_id)
                  }
                  title={full ? t('account.membershipQuotaFull') : undefined}
                  onClick={() => void mutate(sub, 'attach')}
                >
                  {t('account.membershipAttach')}
                </button>
                <button className="btn-ghost" disabled={isBusy || !sel || sub.device_count === 0} onClick={() => void mutate(sub, 'detach')}>
                  {t('account.membershipDetach')}
                </button>
                {full && (
                  <span className="text-secondary" style={{ fontSize: 'var(--font-size-xs)' }}>
                    {t('account.membershipQuotaFull')}
                  </span>
                )}
              </div>

              {/* 管理訂閱：Stripe → Billing Portal（取消／更新付款／換方案都在那裡）；
                  apple/google 是在手機上購買的訂閱 → 引導到 App Store / Google Play 管理。 */}
              {isStripe ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-3)', flexWrap: 'wrap' }}>
                  <button className="btn-ghost" disabled={isManaging} onClick={() => void manage(sub)}>
                    {isManaging ? t('account.membershipManaging') : t('account.membershipManage')}
                  </button>
                  {mErr && (
                    <span className="error-text" role="alert" style={{ fontSize: 'var(--font-size-sm)' }}>
                      {mErr}
                    </span>
                  )}
                </div>
              ) : (
                <p className="text-secondary" style={{ margin: 0, fontSize: 'var(--font-size-sm)' }}>
                  {t('account.membershipManageOnStore')}
                </p>
              )}
              {/* DEV-ONLY test shortcuts (backend /cancel is dev-gated). period_end → cancel_pending
                  (still active until expiry); immediate → hard expire + teardown (clean slate). */}
              {devTools && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-3)', flexWrap: 'wrap' }}>
                  <span className="text-tertiary" style={{ fontSize: 'var(--font-size-xs)', fontWeight: 'var(--font-weight-semibold)' }}>DEV</span>
                  <button
                    className="btn-ghost"
                    disabled={isBusy}
                    style={{ color: 'var(--color-warning, #b8860b)' }}
                    onClick={() => void devCancel(sub, 'period_end')}
                  >
                    {t('account.membershipDevCancelPeriodEnd')}
                  </button>
                  <button
                    className="btn-ghost"
                    disabled={isBusy}
                    style={{ color: 'var(--color-danger, #c0392b)' }}
                    onClick={() => void devCancel(sub, 'immediate')}
                  >
                    {t('account.membershipDevRemoveNow')}
                  </button>
                </div>
              )}
              {err && (
                <p className="error-text" role="alert" style={{ margin: 0, fontSize: 'var(--font-size-sm)' }}>
                  {t('account.membershipMutationFailed', { error: err })}
                </p>
              )}
            </div>
          );
        })
      )}

      {/* R2: just-attached device still on SD → offer switching it to cloud recording. */}
      {switchCloudFor && (
        <ConfirmDialog
          spec={{
            title: t('account.attachSwitchCloudTitle'),
            body: <p style={{ margin: 0 }}>{t('account.attachSwitchCloudBody')}</p>,
            confirmLabel: t('account.attachSwitchCloudConfirm'),
          }}
          onCancel={() => setSwitchCloudFor(null)}
          onConfirm={confirmSwitchCloud}
        />
      )}

      {/* R4: detached cloud recorder with an SD card present → switch to SD, or stop recording. */}
      {detachSdFor && (
        <ConfirmDialog
          spec={{
            title: t('account.detachSdcardTitle'),
            body: <p style={{ margin: 0 }}>{t('account.detachSdcardBody')}</p>,
            confirmLabel: t('account.detachSdcardConfirm'),
            cancelLabel: t('account.detachStopConfirm'),
          }}
          busy={detachBusy}
          onCancel={() => void detachStopRecording()}
          onConfirm={() => void detachUseSdcard()}
        />
      )}
    </section>
  );
}

// ---- page ----

export default function SubscriptionPage() {
  // 生態差異化(08 §2.1.5):brand 生態未授權 subscription:manage → 本功能不提供。
  if (!hasBrandCap('subscription:manage')) {
    return (
      <main style={{ padding: 24 }}>
        <h1>訂閱</h1>
        <p>此生態未提供訂閱功能。/ Subscriptions are not available in this ecosystem.</p>
      </main>
    );
  }

  const { t } = useTranslation();
  const { deviceId } = useParams<{ deviceId: string }>();
  const [device, setDevice] = useState<DeviceLite | null>(null);
  const [sub, setSub] = useState<SubscriptionView | null>(null);
  const [noSub, setNoSub] = useState(false);
  const [quota, setQuota] = useState<QuotaEntry | null>(null);
  const [quotaHidden, setQuotaHidden] = useState(false);
  const [plans, setPlans] = useState<PlanDefinition[]>([]);
  const [explain, setExplain] = useState<EffectiveEntitlementExplain | null>(null);
  const [statement, setStatement] = useState<BillingStatement | null>(null);
  const [loadState, setLoadState] = useState<'loading' | 'ready' | 'error'>('loading');
  const [loadError, setLoadError] = useState<string | null>(null);
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const load = useCallback(async () => {
    if (!deviceId) return;
    setLoadState('loading');
    setLoadError(null);

    // 主資料：訂閱卡（404 = 尚未訂閱，是合法狀態）
    const subP = apiFetch<SubscriptionView>(`/v3/devices/${encodeURIComponent(deviceId)}/subscription`);
    // 輔助資料：device 名稱/role、quota、invoices — 任一失敗都優雅降級
    const devP = apiFetch<DeviceLite>(`/v3/devices/${encodeURIComponent(deviceId)}`);
    const quotaP = apiFetch<QuotaEntry[]>('/v3/me/quota', { query: { device_id: deviceId } });
    const plansP = apiFetch<PlanDefinition[]>('/v3/plans');
    const explainP = apiFetch<EffectiveEntitlementExplain>(`/v3/devices/${encodeURIComponent(deviceId)}/entitlements`);
    const statementP = apiFetch<BillingStatement>('/v3/billing/statement');

    const [subR, devR, quotaR, plansR, explainR, statementR] = await Promise.allSettled([subP, devP, quotaP, plansP, explainP, statementP]);
    if (!alive.current) return;

    // 訂閱
    if (subR.status === 'fulfilled') {
      setSub(subR.value.data ?? null);
      setNoSub(false);
    } else if (subR.reason instanceof ApiError && subR.reason.status === 404) {
      setSub(null);
      setNoSub(true);
    } else if (subR.reason instanceof ApiError && subR.reason.status === 403) {
      // shared-user mask（AC-5）：fail-closed → 顯示遮蔽態
      setSub(null);
      setNoSub(false);
      setLoadError('FORBIDDEN');
    } else {
      setLoadError(subR.reason instanceof ApiError ? subR.reason.message : t('account.networkErrorRetry'));
      setLoadState('error');
      return;
    }

    // device（僅供顯示名稱與 shared 遮蔽預判；失敗不阻斷）
    if (devR.status === 'fulfilled' && devR.value.data && typeof devR.value.data === 'object') {
      setDevice(devR.value.data);
    }

    // quota（02 §7.2 列表信封；端點不可用 → 隱藏）
    if (quotaR.status === 'fulfilled' && Array.isArray(quotaR.value.data)) {
      const entry = quotaR.value.data.find((e) => e.device_id === deviceId) ?? quotaR.value.data[0] ?? null;
      setQuota(entry);
      setQuotaHidden(entry === null);
    } else {
      setQuotaHidden(true);
    }

    setPlans(plansR.status === 'fulfilled' && Array.isArray(plansR.value.data) ? plansR.value.data : []);
    setExplain(explainR.status === 'fulfilled' ? explainR.value.data ?? null : null);
    setStatement(statementR.status === 'fulfilled' ? statementR.value.data ?? null : null);

    setLoadState('ready');
  }, [deviceId, t]);

  useEffect(() => {
    void load();
  }, [load]);

  if (!deviceId) return null;

  if (loadState === 'loading') {
    return (
      <section className="card" style={{ maxWidth: 640 }} aria-busy="true">
        <p className="text-secondary" style={{ margin: 0 }}>
          {t('account.loadingSubscription')}
        </p>
      </section>
    );
  }

  if (loadState === 'error') {
    return (
      <section className="card" style={{ maxWidth: 640, display: 'grid', gap: 'var(--spacing-4)' }}>
        <p className="error-text" role="alert" style={{ margin: 0 }}>
          {t('account.loadSubscriptionFailed', { error: loadError })}
        </p>
        <button className="btn-ghost" style={{ justifySelf: 'start' }} onClick={() => void load()}>
          {t('common.retry')}
        </button>
      </section>
    );
  }

  const deviceLabel = device?.display_name || deviceId;
  const isShared = device?.role != null && device.role !== 'owner';

  // shared-user mask（19 AC-5 / APP 00 §7.2）
  if (isShared || loadError === 'FORBIDDEN') {
    return (
      <section className="card" style={{ maxWidth: 640 }}>
        <h1 style={{ marginTop: 0, fontSize: 'var(--font-size-3xl)' }}>{t('account.subscriptionAndQuota')}</h1>
        <p className="text-secondary" style={{ marginBottom: 0 }}>
          {t('account.sharedDeviceOwnerOnly')}
        </p>
      </section>
    );
  }

  const dims = quota?.quota ? Object.entries(quota.quota) : [];
  const worst = dims.reduce<string>((acc, [, d]) => {
    if (d.status === 'over') return 'over';
    if (d.status === 'warning' && acc !== 'over') return 'warning';
    return acc;
  }, 'ok');
  const overDims = dims.filter(([, d]) => d.status === 'over').map(([k]) => k);
  const planId = sub?.plan_id ?? sub?.plan;
  const selectedPlan = plans.find((p) => p.plan_id === planId && (sub?.plan_version == null || p.version === sub.plan_version));
  const effective = explain?.entitlements ?? sub?.plan_entitlements;
  const rows = statementRows(statement);

  return (
    <div style={{ display: 'grid', gap: 'var(--spacing-8)', maxWidth: 680 }}>
      <header>
        <h1 style={{ margin: 0, fontSize: 'var(--font-size-3xl)' }}>{t('account.subscriptionAndQuota')}</h1>
        <p className="text-secondary" style={{ margin: 'var(--spacing-2) 0 0' }}>
          <Link to={`/devices/${encodeURIComponent(deviceId)}`}>{deviceLabel}</Link>
        </p>
      </header>

      {/* ---- 降級說明 banner（ADR-025 模式 B；19 §6.1） ---- */}
      {worst === 'over' && (
        <div
          role="alert"
          style={{
            border: '1px solid var(--color-semantic-danger)',
            borderRadius: 'var(--radius-md)',
            padding: 'var(--spacing-5) var(--spacing-6)',
            display: 'grid',
            gap: 'var(--spacing-2)',
          }}
        >
          <strong style={{ color: 'var(--color-semantic-danger)' }}>{t('account.recordingDegraded')}</strong>
          <span className="text-secondary" style={{ fontSize: 'var(--font-size-md)' }}>
            {overDims.includes('storage_gb') && (
              <>
                {t('account.degradeStorage', { retention: sub?.retention_days ? t('account.retentionParen', { days: sub.retention_days }) : '' })}{' '}
              </>
            )}
            {overDims.includes('relay_gb') && <>{t('account.degradeRelay')} </>}
            {overDims.includes('share_count') && <>{t('account.degradeShare')}</>}
          </span>
        </div>
      )}
      {worst === 'warning' && (
        <div
          role="status"
          style={{
            border: '1px solid var(--color-semantic-warning)',
            borderRadius: 'var(--radius-md)',
            padding: 'var(--spacing-5) var(--spacing-6)',
          }}
        >
          <span style={{ color: 'var(--color-semantic-warning)' }}>{t('account.usageNearLimit')}</span>
          <span className="text-secondary" style={{ fontSize: 'var(--font-size-md)' }}>
            {' '}
            {t('account.usageNearLimitDetail')}
          </span>
        </div>
      )}

      {/* ---- 訂閱卡 ---- */}
      <section className="card" style={{ display: 'grid', gap: 'var(--spacing-4)' }}>
        <h2 style={{ margin: 0, fontSize: 'var(--font-size-xl)' }}>{t('account.subscriptionPlan')}</h2>
        {noSub ? (
          <>
            <p className="text-secondary" style={{ margin: 0 }}>
              {t('account.noSubscription')}
            </p>
          </>
        ) : sub ? (
          <>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--spacing-4)', flexWrap: 'wrap' }}>
              <span style={{ fontSize: 'var(--font-size-2xl)', fontWeight: 'var(--font-weight-semibold)' }}>{selectedPlan?.name ?? planId ?? '—'}</span>
              <span style={{ color: statusUi(sub, t).color, fontSize: 'var(--font-size-md)' }}>{statusUi(sub, t).label}</span>
            </div>
            <p className="text-secondary" style={{ margin: 0, fontSize: 'var(--font-size-sm)' }}>
              {planId ? `${planId}@${sub.plan_version ?? selectedPlan?.version ?? '—'}` : '—'}
              {selectedPlan?.prices?.[0] ? ` · ${formatAmount(selectedPlan.prices[0].amount_minor, selectedPlan.prices[0].currency)} / ${selectedPlan.prices[0].billing_interval ?? selectedPlan.billing_interval ?? '—'}` : ''}
            </p>
            <dl
              style={{
                display: 'grid',
                gridTemplateColumns: 'max-content 1fr',
                gap: 'var(--spacing-2) var(--spacing-8)',
                margin: 0,
                fontSize: 'var(--font-size-md)',
              }}
            >
              {sub.expired_time != null && (
                <>
                  <dt className="text-secondary">{t('account.expiryDate')}</dt>
                  <dd style={{ margin: 0 }}>{formatTimestampUs(sub.expired_time, { second: undefined })}</dd>
                </>
              )}
              <dt className="text-secondary">{t('account.autoRenew')}</dt>
              <dd style={{ margin: 0 }}>{sub.status === 'cancelled' ? t('account.autoRenewCancelled') : sub.auto_renew ? t('account.autoRenewOn') : t('account.autoRenewOff')}</dd>
              {sub.retention_days != null && (
                <>
                  <dt className="text-secondary">{t('account.recordingRetention')}</dt>
                  <dd style={{ margin: 0 }}>{t('account.daysValue', { count: sub.retention_days })}</dd>
                </>
              )}
              {sub.quality_allowed && (
                <>
                  <dt className="text-secondary">{t('account.maxQuality')}</dt>
                  <dd style={{ margin: 0 }}>{sub.quality_allowed}</dd>
                </>
              )}
            </dl>
          </>
        ) : null}
        <p className="text-secondary" style={{ margin: 0, fontSize: 'var(--font-size-xs)' }}>
          {t('account.purchaseNote')}
        </p>
      </section>

      {effective && (
        <section className="card" style={{ display: 'grid', gap: 'var(--spacing-4)' }}>
          <h2 style={{ margin: 0, fontSize: 'var(--font-size-xl)' }}>{t('account.effectiveEntitlements')}</h2>
          {explain && (
            <p style={{ margin: 0 }}>
              <strong>{t(`account.entitlementDecision.${explain.decision}`)}</strong>
              {!explain.complete && <span className="text-secondary"> · {t('account.entitlementIncomplete')}</span>}
            </p>
          )}
          <dl style={{ display: 'grid', gridTemplateColumns: 'max-content 1fr', gap: 'var(--spacing-2) var(--spacing-8)', margin: 0, fontSize: 'var(--font-size-md)' }}>
            <dt className="text-secondary">{t('account.recordingRetention')}</dt><dd style={{ margin: 0 }}>{effective.retention_days ?? 0} days</dd>
            <dt className="text-secondary">{t('account.maxQuality')}</dt><dd style={{ margin: 0 }}>{effective.quality_max || '—'}</dd>
            <dt className="text-secondary">Storage / relay</dt><dd style={{ margin: 0 }}>{effective.storage_gb ?? 0} GB / {effective.relay_gb ?? 0} GB</dd>
            <dt className="text-secondary">AI</dt><dd style={{ margin: 0 }}>{effective.ai_features?.join(', ') || '—'} ({effective.ai_monthly_units ?? 0})</dd>
            <dt className="text-secondary">Share</dt><dd style={{ margin: 0 }}>{effective.share_users ?? 0}</dd>
          </dl>
          {explain && <div aria-label="entitlement-source-chain" style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--spacing-2)' }}>
            {explain.stages.map((stage) => <span key={stage.layer} title={stage.code} style={{ border: '1px solid var(--color-surface-line)', borderRadius: 'var(--radius-full)', padding: '2px var(--spacing-4)', fontSize: 'var(--font-size-xs)' }}>{stage.layer}: {stage.status}</span>)}
          </div>}
        </section>
      )}

      {/* ---- 配額儀表板（02 §7.2；端點不可用即隱藏） ---- */}
      {!quotaHidden && quota?.quota && dims.length > 0 && (
        <section className="card" style={{ display: 'grid', gap: 'var(--spacing-5)' }}>
          <h2 style={{ margin: 0, fontSize: 'var(--font-size-xl)' }}>{t('account.usageAndQuota')}</h2>
          {dims.map(([key, d]) => {
            const label = DIMENSION_LABEL_KEY[key] ? t(DIMENSION_LABEL_KEY[key]) : key;
            const unit = DIMENSION_UNIT_KEY[key] ? t(DIMENSION_UNIT_KEY[key]) : '';
            const hasLimit = d.limit !== null && d.limit !== undefined && d.limit > 0;
            const ratio = hasLimit && d.used != null ? Math.min(1, d.used / (d.limit as number)) : null;
            return (
              <div key={key} style={{ display: 'grid', gap: 'var(--spacing-2)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--spacing-4)', fontSize: 'var(--font-size-md)' }}>
                  <span>{label}</span>
                  <span style={{ color: dimColor(d.status) }}>
                    {d.used == null ? '—' : `${d.used}${unit}`}
                    {hasLimit ? ` / ${d.limit}${unit}` : ''}
                    {!hasLimit && d.status === 'unknown' ? t('account.limitUndefined') : ''}
                  </span>
                </div>
                {/* limit=null → 不顯示進度條/百分比（19 §5.1 安全語意） */}
                {ratio !== null && (
                  <div style={{ height: 6, borderRadius: 'var(--radius-full)', background: 'var(--color-surface-panel2)', overflow: 'hidden' }}>
                    <div style={{ width: `${Math.round(ratio * 100)}%`, height: '100%', background: dimColor(d.status) }} />
                  </div>
                )}
                {(d.period_start != null || d.as_of != null) && (
                  <div className="text-secondary" style={{ fontSize: 'var(--font-size-xs)' }}>
                    {d.period_start != null && d.period_end != null
                      ? t('account.quotaPeriod', {
                          start: formatTimestampUs(d.period_start, { hour: undefined, minute: undefined, second: undefined }),
                          end: formatTimestampUs(d.period_end, { hour: undefined, minute: undefined, second: undefined }),
                        })
                      : ''}
                    {d.as_of != null ? t('account.quotaAsOf', { date: formatTimestampUs(d.as_of, { second: undefined }) }) : ''}
                  </div>
                )}
              </div>
            );
          })}
          <p className="text-secondary" style={{ margin: 0, fontSize: 'var(--font-size-xs)' }}>
            {t('account.quotaFootnote')}
          </p>
        </section>
      )}

      {/* ---- 帳單歷史（00 §9.1 近 20 筆；失敗即隱藏） ---- */}
      {statement !== null && (
        <section className="card" style={{ display: 'grid', gap: 'var(--spacing-4)' }}>
          <h2 style={{ margin: 0, fontSize: 'var(--font-size-xl)' }}>{t('account.billingHistory')}</h2>
          {rows.length === 0 ? (
            <p className="text-secondary" style={{ margin: 0 }}>
              {t('account.noBillingRecords')}
            </p>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--font-size-md)' }}>
              <thead>
                <tr className="text-secondary" style={{ textAlign: 'left', fontSize: 'var(--font-size-sm)' }}>
                  <th style={{ padding: 'var(--spacing-2) 0', fontWeight: 'var(--font-weight-medium)' }}>{t('account.colDate')}</th>
                  <th style={{ padding: 'var(--spacing-2) 0', fontWeight: 'var(--font-weight-medium)' }}>{t('account.colType')}</th>
                  <th style={{ padding: 'var(--spacing-2) 0', fontWeight: 'var(--font-weight-medium)' }}>{t('account.colPlan')}</th>
                  <th style={{ padding: 'var(--spacing-2) 0', fontWeight: 'var(--font-weight-medium)', textAlign: 'right' }}>{t('account.colAmount')}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={`${r.type}-${r.id}`} style={{ borderTop: '1px solid var(--color-surface-line)' }}>
                    <td style={{ padding: 'var(--spacing-3) 0' }}>{formatTimestampUs(r.ts, { second: undefined })}</td>
                    <td style={{ padding: 'var(--spacing-3) 0' }}>
                      <span style={{ color: r.type.startsWith('refund') || r.type.startsWith('credit') ? 'var(--color-semantic-success)' : undefined }}>
                        {r.type}
                      </span>
                    </td>
                    <td style={{ padding: 'var(--spacing-3) 0' }}>{r.detail}</td>
                    <td style={{ padding: 'var(--spacing-3) 0', textAlign: 'right', fontFamily: 'var(--font-family-mono)' }}>
                      {r.amountMinor == null ? '—' : formatAmount(r.amountMinor, r.currency)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <p className="text-secondary" style={{ margin: 0, fontSize: 'var(--font-size-xs)' }}>
            {t('account.billingFootnote')}
          </p>
        </section>
      )}

      {/* 帳號層「雲端方案會員」已移出 per-device 頁，改由獨立的 /account/subscription 帳號頁提供
          (MembershipPage → MembershipSection);此處只保留該裝置的訂閱狀態/配額/帳單檢視。 */}
    </div>
  );
}
